import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ExtractionMode = components["schemas"]["BillExtractionMode"];
type ExtractionModel = components["schemas"]["BillExtractionModel"];

const MODE_OPTIONS: { value: ExtractionMode; label: string }[] = [
  { value: "Ocr", label: "OCR (Google Document AI)" },
  { value: "Llm", label: "LLM (vision extraction)" },
];

const MODEL_OPTIONS: { value: ExtractionModel; label: string }[] = [
  { value: "GeminiFlash", label: "Gemini Flash" },
  { value: "ClaudeSonnet", label: "Claude Sonnet" },
  { value: "ClaudeHaiku", label: "Claude Haiku" },
];

interface HiddenClientSettingsSectionProps {
  clientId: string;
}

export function HiddenClientSettingsSection({ clientId }: HiddenClientSettingsSectionProps) {
  const [mode, setMode] = useState<ExtractionMode>("Ocr");
  const [model, setModel] = useState<ExtractionModel>("GeminiFlash");
  // The last-saved values, so we can tell whether the form is dirty and reset on cancel.
  const [saved, setSaved] = useState<{ mode: ExtractionMode; model: ExtractionModel } | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/admin/clients/{clientId}/hidden-settings", {
        params: { path: { clientId } },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("Failed to load hidden settings.");
        } else {
          setMode(data.billExtractionMode);
          setModel(data.billExtractionModel);
          setSaved({ mode: data.billExtractionMode, model: data.billExtractionModel });
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const dirty = saved !== null && (saved.mode !== mode || saved.model !== model);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setJustSaved(false);
    const { data, error: apiError } = await api.PATCH("/admin/clients/{clientId}/hidden-settings", {
      params: { path: { clientId } },
      body: { billExtractionMode: mode, billExtractionModel: model },
    });
    if (apiError || !data) {
      setError("Failed to save hidden settings.");
      setSaving(false);
      return;
    }
    setMode(data.billExtractionMode);
    setModel(data.billExtractionModel);
    setSaved({ mode: data.billExtractionMode, model: data.billExtractionModel });
    setSaving(false);
    setJustSaved(true);
  }

  function handleReset() {
    if (!saved) return;
    setMode(saved.mode);
    setModel(saved.model);
    setError(null);
    setJustSaved(false);
  }

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hidden Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm">
          Dolfin-managed settings for this client, applied to every organisation under it. These are
          hidden from the client — only staff can view or change them here.
        </p>

        <div className="grid max-w-md grid-cols-1 gap-5">
          <div className="space-y-2">
            <Label htmlFor="hidden-extraction-mode">Bill extraction mode</Label>
            <Select
              value={mode}
              onValueChange={(v) => {
                setMode(v as ExtractionMode);
                setJustSaved(false);
              }}
            >
              <SelectTrigger id="hidden-extraction-mode" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              How inbound bill documents are extracted. LLM falls back to OCR if the model call fails.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hidden-extraction-model">Extraction model</Label>
            <Select
              value={model}
              onValueChange={(v) => {
                setModel(v as ExtractionModel);
                setJustSaved(false);
              }}
              disabled={mode !== "Llm"}
            >
              <SelectTrigger id="hidden-extraction-model" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODEL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Which LLM extracts the bill. Only used when the mode is LLM.
            </p>
          </div>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving || !dirty}>
            {saving && <Spinner className="size-4" />}
            Save
          </Button>
          <Button variant="ghost" onClick={handleReset} disabled={saving || !dirty}>
            Reset
          </Button>
          {justSaved && !dirty && <span className="text-muted-foreground text-sm">Saved.</span>}
        </div>
      </CardContent>
    </Card>
  );
}
